import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  BookOpen, Plus, FolderOpen, Edit2, Trash2, ChevronRight, 
  User, LogOut, Library, ArrowLeft, MoreVertical, Search,
  Target, FileText, ClipboardList, Users, Share2, Eye, Pencil, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentFolders = ({ user }) => {
  const navigate = useNavigate();
  const { folderId } = useParams();
  
  const [folders, setFolders] = useState([]);
  const [sharedFolders, setSharedFolders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderShares, setFolderShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [folderToDelete, setFolderToDelete] = useState(null);
  
  // Form states
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderNotes, setNewFolderNotes] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (folderId) {
      fetchFolder();
      fetchFolderShares();
    } else {
      fetchFolders();
      fetchSharedFolders();
    }
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folderId]);

  const fetchFolders = async () => {
    try {
      const response = await axios.get(`${API}/folders`, {
        withCredentials: true
      });
      setFolders(response.data);
      setCurrentFolder(null);
    } catch (error) {
      console.error('Error fetching folders:', error);
      toast.error('Failed to load folders');
    } finally {
      setLoading(false);
    }
  };

  const fetchSharedFolders = async () => {
    try {
      const response = await axios.get(`${API}/folders/shared-with-me`, { withCredentials: true });
      setSharedFolders(response.data.shared_folders || []);
    } catch (error) {
      console.error('Error fetching shared folders:', error);
    }
  };

  const fetchTeams = async () => {
    try {
      const response = await axios.get(`${API}/teams`, { withCredentials: true });
      setTeams(response.data.teams || []);
    } catch (error) {
      console.error('Error fetching teams:', error);
    }
  };

  const fetchFolderShares = async () => {
    if (!folderId) return;
    try {
      const response = await axios.get(`${API}/folders/${folderId}/shares`, { withCredentials: true });
      setFolderShares(response.data.shares || []);
    } catch (error) {
      console.error('Error fetching folder shares:', error);
    }
  };

  const fetchFolder = async () => {
    try {
      const response = await axios.get(`${API}/folders/${folderId}`, {
        withCredentials: true
      });
      setCurrentFolder(response.data);
    } catch (error) {
      console.error('Error fetching folder:', error);
      toast.error('Folder not found');
      navigate('/students');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a student name');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/folders`, {
        student_name: newFolderName.trim(),
        notes: newFolderNotes.trim() || null
      }, { withCredentials: true });
      
      setFolders([...folders, { ...response.data, story_count: 0 }]);
      setCreateDialogOpen(false);
      setNewFolderName('');
      setNewFolderNotes('');
      toast.success('Student folder created!');
    } catch (error) {
      console.error('Error creating folder:', error);
      toast.error('Failed to create folder');
    } finally {
      setSaving(false);
    }
  };

  const handleEditFolder = async () => {
    if (!folderToEdit || !newFolderName.trim()) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/folders/${folderToEdit.folder_id}`, {
        student_name: newFolderName.trim(),
        notes: newFolderNotes.trim() || null
      }, { withCredentials: true });
      
      setFolders(folders.map(f => 
        f.folder_id === folderToEdit.folder_id 
          ? { ...f, student_name: newFolderName.trim(), notes: newFolderNotes.trim() }
          : f
      ));
      setEditDialogOpen(false);
      setFolderToEdit(null);
      toast.success('Folder updated!');
    } catch (error) {
      console.error('Error updating folder:', error);
      toast.error('Failed to update folder');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    
    try {
      await axios.delete(`${API}/folders/${folderToDelete.folder_id}`, {
        withCredentials: true
      });
      
      setFolders(folders.filter(f => f.folder_id !== folderToDelete.folder_id));
      setDeleteDialogOpen(false);
      setFolderToDelete(null);
      toast.success('Folder deleted');
    } catch (error) {
      console.error('Error deleting folder:', error);
      toast.error('Failed to delete folder');
    }
  };

  const handleShareFolder = async () => {
    if (!selectedTeamId || !currentFolder) {
      toast.error('Please select a team');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/folders/${currentFolder.folder_id}/share`, {
        team_id: selectedTeamId,
        permission: sharePermission
      }, { withCredentials: true });
      
      toast.success('Folder shared with team!');
      setShareDialogOpen(false);
      setSelectedTeamId('');
      setSharePermission('view');
      fetchFolderShares();
    } catch (error) {
      console.error('Error sharing folder:', error);
      toast.error(error.response?.data?.detail || 'Failed to share folder');
    } finally {
      setSaving(false);
    }
  };

  const handleUnshareFolder = async (teamId) => {
    if (!currentFolder) return;
    
    try {
      await axios.delete(`${API}/folders/${currentFolder.folder_id}/share/${teamId}`, { withCredentials: true });
      toast.success('Sharing removed');
      fetchFolderShares();
    } catch (error) {
      console.error('Error removing share:', error);
      toast.error('Failed to remove sharing');
    }
  };

  const openEditDialog = (folder) => {
    setFolderToEdit(folder);
    setNewFolderName(folder.student_name);
    setNewFolderNotes(folder.notes || '');
    setEditDialogOpen(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/');
    } catch (error) {
      toast.error('Failed to logout');
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  };

  const filteredFolders = folders.filter(folder => 
    folder.student_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredStories = currentFolder?.stories?.filter(story =>
    story.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    story.scenario?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // View for a specific folder
  if (folderId && currentFolder) {
    return (
      <div className="min-h-screen bg-background" data-testid="folder-view-page">
        {/* Header */}
        <header className="w-full px-6 py-4 bg-white shadow-soft">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
              <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
            </div>
            
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/students')}
                variant="ghost"
                className="text-text-secondary hover:text-primary"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                All Students
              </Button>
            </div>
          </div>
        </header>

        {/* Folder Header */}
        <div className="bg-secondary/20 px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="font-heading font-bold text-2xl sm:text-3xl text-text-primary">
                  {currentFolder.student_name}
                </h1>
                {currentFolder.notes && (
                  <p className="font-body text-text-secondary mt-1">{currentFolder.notes}</p>
                )}
                <p className="font-body text-sm text-text-muted mt-1">
                  {currentFolder.stories?.length || 0} stories
                </p>
              </div>
            </div>
            
            {/* Quick Action Buttons */}
            <div className="flex flex-wrap gap-3 mt-6">
              <Button
                onClick={() => navigate('/create')}
                className="bg-primary text-white hover:bg-primary/90 rounded-full px-4 py-2"
                data-testid="new-story-btn"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                New Story
              </Button>
              <Button
                onClick={() => navigate('/iep-goals')}
                variant="outline"
                className="rounded-full px-4 py-2 border-[#6366F1] text-[#6366F1] hover:bg-[#6366F1]/10"
                data-testid="new-goal-btn"
              >
                <Target className="w-4 h-4 mr-2" />
                New Goal
              </Button>
              <Button
                onClick={() => navigate('/present-levels')}
                variant="outline"
                className="rounded-full px-4 py-2 border-amber-500 text-amber-600 hover:bg-amber-50"
                data-testid="new-present-levels-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                New Present Levels
              </Button>
              <Button
                onClick={() => navigate('/worksheets')}
                variant="outline"
                className="rounded-full px-4 py-2 border-purple-500 text-purple-600 hover:bg-purple-50"
                data-testid="new-worksheet-btn"
              >
                <ClipboardList className="w-4 h-4 mr-2" />
                New Worksheet
              </Button>
              <Button
                onClick={() => setShareDialogOpen(true)}
                variant="outline"
                className="rounded-full px-4 py-2 border-teal-500 text-teal-600 hover:bg-teal-50"
                data-testid="share-folder-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share with Team
              </Button>
            </div>

            {/* Current Shares */}
            {folderShares.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-sm text-text-muted">Shared with:</span>
                {folderShares.map((share) => (
                  <Badge 
                    key={share.team_id} 
                    variant="outline" 
                    className="bg-teal-50 text-teal-700 border-teal-200 flex items-center gap-1"
                  >
                    <Users className="w-3 h-3" />
                    {share.team_name}
                    <span className="text-teal-500">
                      ({share.permission === 'edit' ? <Pencil className="w-3 h-3 inline" /> : <Eye className="w-3 h-3 inline" />})
                    </span>
                    <button 
                      onClick={() => handleUnshareFolder(share.team_id)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Stories in Folder */}
        <main className="px-6 py-8 max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-heading font-semibold text-xl text-text-primary">Stories</h2>
            <Button
              onClick={() => navigate('/create')}
              className="bg-primary text-white hover:bg-primary/90 rounded-full px-4 py-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Story
            </Button>
          </div>

          {/* Search */}
          <div className="relative mb-6 max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search stories..."
              className="pl-12 border-2 border-primary/20 rounded-xl"
            />
          </div>

          {filteredStories.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStories.map((story) => (
                <div 
                  key={story.story_id}
                  onClick={() => navigate(`/story/${story.story_id}`)}
                  className="bg-white rounded-2xl p-6 shadow-card hover:shadow-float transition-all duration-300 cursor-pointer hover:-translate-y-1"
                >
                  <div className="w-full h-32 bg-gradient-to-br from-primary/10 to-secondary rounded-xl mb-4 flex items-center justify-center">
                    <BookOpen className="w-12 h-12 text-primary/30" />
                  </div>
                  <h3 className="font-heading font-semibold text-text-primary mb-1 line-clamp-1">
                    {story.title}
                  </h3>
                  <p className="font-body text-sm text-text-muted">{story.scenario}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-12 shadow-card text-center">
              <div className="w-16 h-16 bg-secondary/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-primary/50" />
              </div>
              <h3 className="font-heading font-semibold text-lg text-text-primary mb-2">
                No stories yet
              </h3>
              <p className="font-body text-text-secondary mb-4">
                Create a story for {currentFolder.student_name}
              </p>
              <Button
                onClick={() => navigate('/create')}
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
              >
                Create Story
              </Button>
            </div>
          )}
        </main>
      </div>
    );
  }

  // Main folders list view
  return (
    <div className="min-h-screen bg-background" data-testid="student-folders-page">
      {/* Header */}
      <header className="w-full px-6 py-4 bg-white shadow-soft">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="cursor-pointer" onClick={() => navigate('/dashboard')}>
            <SpEdKitLogo className="w-10 h-10" textClassName="text-2xl" />
          </div>
          
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/my-stories')}
              variant="ghost"
              className="hidden sm:flex items-center gap-2 text-text-secondary hover:text-primary hover:bg-primary/10"
            >
              <Library className="w-5 h-5" />
              All Stories
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                  <Avatar className="h-10 w-10 border-2 border-primary/20">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-heading">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end">
                <div className="px-3 py-2">
                  <p className="font-heading font-semibold text-text-primary">{user?.name}</p>
                  <p className="text-sm text-text-muted truncate">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/dashboard')} className="cursor-pointer">
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/my-stories')} className="cursor-pointer">
                  <Library className="mr-2 h-4 w-4" />
                  All Stories
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-6 py-12 max-w-7xl mx-auto">
        {/* Title & Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading font-bold text-3xl text-text-primary mb-1">
              Student Folders
            </h1>
            <p className="font-body text-text-secondary">
              Organize stories by student
            </p>
          </div>
          
          <Button
            onClick={() => {
              setNewFolderName('');
              setNewFolderNotes('');
              setCreateDialogOpen(true);
            }}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-2 shadow-soft"
            data-testid="create-folder-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Student
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-8 max-w-md">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-muted" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search students..."
            className="pl-12 border-2 border-primary/20 rounded-xl"
            data-testid="search-folders-input"
          />
        </div>

        {/* Folders Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-card animate-pulse">
                <div className="w-14 h-14 bg-secondary/50 rounded-xl mb-4"></div>
                <div className="h-5 bg-secondary/50 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-secondary/50 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : filteredFolders.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFolders.map((folder) => (
              <div 
                key={folder.folder_id}
                className="bg-white rounded-2xl p-6 shadow-card hover:shadow-float transition-all duration-300 group"
                data-testid={`folder-card-${folder.folder_id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div 
                    className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center cursor-pointer"
                    onClick={() => navigate(`/students/${folder.folder_id}`)}
                  >
                    <FolderOpen className="w-7 h-7 text-primary" />
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem 
                        onClick={() => openEditDialog(folder)}
                        className="cursor-pointer"
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => {
                          setFolderToDelete(folder);
                          setDeleteDialogOpen(true);
                        }}
                        className="cursor-pointer text-destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                
                <div 
                  className="cursor-pointer"
                  onClick={() => navigate(`/students/${folder.folder_id}`)}
                >
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-1">
                    {folder.student_name}
                  </h3>
                  {folder.notes && (
                    <p className="font-body text-sm text-text-secondary line-clamp-2 mb-2">
                      {folder.notes}
                    </p>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="font-body text-sm text-text-muted">
                      {folder.story_count || 0} {folder.story_count === 1 ? 'story' : 'stories'}
                    </span>
                    <ChevronRight className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-card text-center">
            <div className="w-20 h-20 bg-secondary/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <FolderOpen className="w-10 h-10 text-primary/50" />
            </div>
            {searchQuery ? (
              <>
                <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                  No students found
                </h3>
                <p className="font-body text-text-secondary mb-6">
                  No students match "{searchQuery}"
                </p>
                <Button
                  onClick={() => setSearchQuery('')}
                  variant="outline"
                  className="border-2 border-primary text-primary hover:bg-primary/10 rounded-full"
                >
                  Clear Search
                </Button>
              </>
            ) : (
              <>
                <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
                  No student folders yet
                </h3>
                <p className="font-body text-text-secondary mb-6">
                  Create folders to organize stories by student name
                </p>
                <Button
                  onClick={() => setCreateDialogOpen(true)}
                  className="bg-primary text-white hover:bg-primary/90 rounded-full px-8 py-3"
                >
                  Add Your First Student
                </Button>
              </>
            )}
          </div>
        )}

        {/* Shared Folders Section */}
        {sharedFolders.length > 0 && (
          <div className="mt-12" data-testid="shared-folders-section">
            <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
              <Share2 className="w-5 h-5 text-teal-600" />
              Shared with Me
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sharedFolders.map((folder) => (
                <div
                  key={folder.folder_id}
                  className="group bg-white rounded-2xl p-6 shadow-card hover:shadow-float transition-all duration-300 border-2 border-teal-100 cursor-pointer"
                  onClick={() => navigate(`/students/${folder.folder_id}`)}
                  data-testid={`shared-folder-${folder.folder_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-teal-600" />
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`${
                        folder.permission === 'edit' 
                          ? 'bg-teal-50 text-teal-700 border-teal-200' 
                          : 'bg-gray-50 text-gray-600 border-gray-200'
                      }`}
                    >
                      {folder.permission === 'edit' ? (
                        <><Pencil className="w-3 h-3 mr-1" /> Edit</>
                      ) : (
                        <><Eye className="w-3 h-3 mr-1" /> View</>
                      )}
                    </Badge>
                  </div>
                  
                  <h3 className="font-heading font-semibold text-lg text-text-primary mb-1">
                    {folder.student_name}
                  </h3>
                  <p className="font-body text-sm text-text-muted mb-2">
                    Shared by {folder.shared_by} • {folder.team_name}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="font-body text-sm text-text-muted">
                      {folder.story_count || 0} {folder.story_count === 1 ? 'story' : 'stories'}
                    </span>
                    <ChevronRight className="w-5 h-5 text-teal-600" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Create Folder Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Add Student Folder</DialogTitle>
            <DialogDescription className="font-body">
              Create a folder to organize stories for a specific student
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-body font-semibold">Student Name</Label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="e.g., Alex Johnson"
                className="mt-2"
                data-testid="folder-name-input"
              />
            </div>
            <div>
              <Label className="font-body font-semibold">Notes (optional)</Label>
              <Textarea
                value={newFolderNotes}
                onChange={(e) => setNewFolderNotes(e.target.value)}
                placeholder="e.g., Grade 2, Mrs. Smith's class"
                className="mt-2"
                data-testid="folder-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateFolder} 
              disabled={saving}
              className="bg-primary text-white rounded-full"
              data-testid="save-folder-btn"
            >
              {saving ? 'Creating...' : 'Create Folder'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Folder Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Student Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="font-body font-semibold">Student Name</Label>
              <Input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                className="mt-2"
              />
            </div>
            <div>
              <Label className="font-body font-semibold">Notes (optional)</Label>
              <Textarea
                value={newFolderNotes}
                onChange={(e) => setNewFolderNotes(e.target.value)}
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            <Button 
              onClick={handleEditFolder} 
              disabled={saving}
              className="bg-primary text-white rounded-full"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete folder?</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              This will delete the folder for "{folderToDelete?.student_name}". 
              The stories inside will not be deleted, just unassigned from this folder.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteFolder}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Folder Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Share2 className="w-5 h-5 text-teal-600" />
              Share Folder with Team
            </DialogTitle>
            <DialogDescription className="font-body">
              Share "{currentFolder?.student_name}" with a team to collaborate.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {teams.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 rounded-lg">
                <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-text-muted mb-3">You're not part of any teams yet</p>
                <Button
                  onClick={() => navigate('/teams')}
                  size="sm"
                  className="bg-teal-600 text-white hover:bg-teal-700 rounded-full"
                >
                  Create or Join a Team
                </Button>
              </div>
            ) : (
              <>
                <div>
                  <Label className="font-body font-semibold">Select Team *</Label>
                  <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                    <SelectTrigger className="mt-2" data-testid="share-team-select">
                      <SelectValue placeholder="Choose a team..." />
                    </SelectTrigger>
                    <SelectContent>
                      {teams.map((team) => (
                        <SelectItem key={team.team_id} value={team.team_id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="font-body font-semibold">Permission Level</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setSharePermission('view')}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        sharePermission === 'view' 
                          ? 'border-teal-500 bg-teal-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="permission-view"
                    >
                      <Eye className={`w-5 h-5 mb-1 ${sharePermission === 'view' ? 'text-teal-600' : 'text-gray-400'}`} />
                      <p className="font-medium text-sm">View Only</p>
                      <p className="text-xs text-text-muted">Can see but not edit</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setSharePermission('edit')}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        sharePermission === 'edit' 
                          ? 'border-teal-500 bg-teal-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid="permission-edit"
                    >
                      <Pencil className={`w-5 h-5 mb-1 ${sharePermission === 'edit' ? 'text-teal-600' : 'text-gray-400'}`} />
                      <p className="font-medium text-sm">Can Edit</p>
                      <p className="text-xs text-text-muted">Full access to folder</p>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShareDialogOpen(false)} className="rounded-full">
              Cancel
            </Button>
            {teams.length > 0 && (
              <Button 
                onClick={handleShareFolder} 
                disabled={saving || !selectedTeamId}
                className="bg-teal-600 text-white hover:bg-teal-700 rounded-full"
                data-testid="share-folder-submit"
              >
                {saving ? 'Sharing...' : 'Share Folder'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentFolders;
